/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../constants/constants.js";
import usePluginUtils from "./usePluginUtils.js";
import { useMemo } from "react";
import { useAdditionalFields } from "../lib/react-query/useAdditionalFields.js";
import { useBooking } from "./useBooking.js";
import { useCart } from "./useCart";
import { useConfig } from "../lib/react-query/useConfig";
import { useSelector } from "react-redux";
import { FlowType } from "../store/slices/flowSlice";
import { buildProductsForRequest } from "../store/slices/bookingSlice";
import {usePreparedProducts} from "./usePreparedProducts";

export const useStepManager = () => {
  const navigate = useNavigate();
  const { data: config } = useConfig();
  const location = useLocation();
  const { isPluginEnabled } = usePluginUtils();
  const { selectedServices, selectedProvider, selectedTime, timeValidatedProducts, invoice } = useBooking();
  const { productLines } = useCart();
  const flow = useSelector((state) => state.flow.activeFlow);
  const selectedCategory = useSelector((state) => state.products.selectedCategory);
  const needsBookingProducts = Boolean(selectedTime && selectedProvider && selectedServices.length);
  const { products: bookingProducts } = usePreparedProducts({ enabled: needsBookingProducts });
  const checkForAdditionalFields = useMemo(
    () => isPluginEnabled("event_field") && location.pathname === ROUTES.DETAILS,
    [isPluginEnabled, location.pathname]
  );

  const { data: additional_fields } = useAdditionalFields(
    {
      "filter[service_id]": selectedServices.map((service) => service.id),
    },
    { enabled: checkForAdditionalFields }
  );

  const stepLogic = useMemo(() => {
    const currentPath = location.pathname;

    const stepsByFlow = {
      [FlowType.BOOKING]: [
        {
          condition: () => !selectedServices.length && selectedProvider,
          route: ROUTES.SERVICES,
        },
        {
          condition: () => {
            if (!selectedServices.length || !isPluginEnabled("paid_attributes")) return false;
            if (currentPath !== ROUTES.SERVICES) return false;
            if (!selectedServices.some((s) => s.has_available_addons)) return false;
            return true;
          },
          route: ROUTES.ADDONS,
        },
        {
          condition: () => {
            if (currentPath !== ROUTES.ADDONS) return false;
            if (!selectedProvider || !selectedTime) return false;
            const currentProducts = JSON.stringify(buildProductsForRequest(selectedServices));
            if (currentProducts === JSON.stringify(timeValidatedProducts)) return false;
            return true;
          },
          route: ROUTES.DATETIME,
        },
        {
          condition: () => {
            if (!selectedServices.length || selectedProvider) return false;
            if (![ROUTES.SERVICES, ROUTES.ADDONS].includes(currentPath)) return false;
            return true;
          },
          route: ROUTES.PROVIDERS,
        },
        {
          condition: () => selectedServices.length && selectedProvider && !selectedTime,
          route: ROUTES.DATETIME,
        },
        {
          condition: () => {
            if (!selectedTime || !selectedServices.length || !selectedProvider || !isPluginEnabled("product"))
              return false;
            if (![ROUTES.DATETIME, ROUTES.PROVIDERS, ROUTES.SERVICES, ROUTES.ADDONS].includes(currentPath)) return false;
            if (!selectedServices.some((s) => s.has_available_products)) return false;
            return true;
          },
          route: ROUTES.PRODUCTS,
        },
        {
          condition: () => {
            if (!selectedTime || !selectedServices.length || !selectedProvider) return false;
            if (currentPath === ROUTES.DETAILS) return false;
            return true;
          },
          route: () => {
            if (
              currentPath === ROUTES.HOME &&
              isPluginEnabled("paid_attributes") &&
              selectedServices.some((s) => s.has_available_addons)
            ) {
              return ROUTES.ADDONS;
            }
            if (isPluginEnabled("product") && currentPath !== ROUTES.PRODUCTS) {
              return ROUTES.PRODUCTS;
            }
            return ROUTES.DETAILS;
          },
        },
        {
          condition: () => currentPath === ROUTES.DETAILS && isPluginEnabled("event_field"),
          route: () => {
            return additional_fields?.length ? ROUTES.ADDITIONAL_FIELDS : null;
          },
        },
        {
          condition: () => (currentPath === ROUTES.ADDITIONAL_FIELDS || currentPath === ROUTES.DETAILS) && invoice,
          route: ROUTES.CHECKOUT,
        },
        {
          condition: () => (currentPath === ROUTES.ADDITIONAL_FIELDS || currentPath === ROUTES.DETAILS) && !invoice,
          route: ROUTES.BOOKING_CONFIRMED,
        },
      ],
      [FlowType.CART]: [
        {
          condition: () => isPluginEnabled("product") && config.product_use_categories && !selectedCategory,
          route: ROUTES.PRODUCTS_FOR_SALE_CATEGORIES,
        },
        {
          condition: () =>
            isPluginEnabled("product") &&
            selectedCategory &&
            currentPath === ROUTES.PRODUCTS_FOR_SALE_CATEGORIES,
          route: ROUTES.PRODUCTS_FOR_SALE,
        },
        {
          condition: () =>
            isPluginEnabled("product") && productLines.length && currentPath === ROUTES.PRODUCTS_FOR_SALE,
          route: ROUTES.ORDER_SUMMARY,
        },
        {
          condition: () =>
            isPluginEnabled("product") && productLines.length && currentPath === ROUTES.ORDER_SUMMARY,
          route: ROUTES.CHECKOUT,
        },
      ],
    };

    const determineNextStep = () => {
      const steps = stepsByFlow[flow] || [];

      for (const step of steps) {
        if (step.condition()) {
          return typeof step.route === "function" ? step.route() : step.route;
        }
      }

      return ROUTES.HOME;
    };

    return { determineNextStep };
  }, [
    location.pathname,
    selectedServices,
    selectedProvider,
    productLines,
    selectedCategory,
    selectedTime,
    timeValidatedProducts,
    isPluginEnabled,
    invoice,
    additional_fields,
    flow,
  ]);

  const navigateToNextStep = (options = {}) => {
    const nextStep = stepLogic.determineNextStep();

    if (nextStep) navigate(nextStep, options);
  };

  const navigateToPreviousStep = () => {
    navigate(-1);
  };

  const navigateToStep = (route) => {
    navigate(route);
  };

  return {
    navigateToNextStep,
    navigateToPreviousStep,
    navigateToStep,
  };
};
